/**
 * Per-variant traveler form schema factory.
 *
 * The traveler form branches four ways (adult / under18adult / child / infant). The
 * birth-date validation range is variant-specific AND depends on runtime data — the
 * check-in date and the hotel's age bands (from the band probe) — so the schema can't
 * be a static constant. This factory closes over `checkIn` + `bands` and is built ONCE
 * at form mount (both are guaranteed non-null at step 1).
 *
 * Age is always evaluated **at check-in**, not today (a child who is 12 at booking but
 * 13 at check-in is rejected as a child — see validation.calculateAgeAtDate).
 *
 * See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §10.
 */

import { AdultTravelerFormSchema, ChildTravelerFormSchema } from './traveler';
import { birthDateValidator, calculateAgeAtDate, ADULT_MIN_AGE } from './validation';
import { ageRangeForVariant, isEmptyRange, type AgeBands } from './ageBands';
import type { TravelerVariant } from './travelerVariant';
import { parseGermanDateLocal } from '@/utils/formatting/dateParser';

/** Per-variant translation key for an out-of-range birth date. */
const AGE_RANGE_ERROR_KEY: Record<TravelerVariant, string> = {
  adult: 'ibe.validation.traveler.adult-age-range',
  under18adult: 'ibe.validation.traveler.youth-age-range',
  child: 'ibe.validation.traveler.child-age-range',
  infant: 'ibe.validation.traveler.infant-age-range',
};

/**
 * Adult form schema: the existing adult fields (salutation + names + nationality +
 * the optional address fields), but with the birth-date age check evaluated at check-in.
 *
 * A `adult` variant is a *full* adult (hotelClass adult AND ≥18 by deriveVariant), so the
 * floor is a fixed 18 (ADULT_MIN_AGE) — NOT the band's `adultMin`, which can sit below 18
 * and only marks where the hotel stops calling someone a child (the under18adult neighbour).
 */
function createAdultFormSchema(checkIn: Date) {
  return AdultTravelerFormSchema.extend({
    birthDate: birthDateValidator.refine(
      (date) => calculateAgeAtDate(parseGermanDateLocal(date), checkIn) >= ADULT_MIN_AGE,
      AGE_RANGE_ERROR_KEY.adult
    ),
  });
}

/**
 * Gender-style form schema (under18adult / child / infant): the child fields (gender +
 * names + nationality) with a variant-specific birth-date range evaluated at check-in.
 *
 * The loose "< 18 at check-in" rule fires ONLY for an empty `under18adult` range (band
 * unknown → `[childMax+1, 17]` collapses; framework §0.3). Any other empty range rejects all.
 *
 * NOTE: the `variant` comes from the Player PassengerCode (hotelClass) while the
 * `bands` come from the independent DaVinci band probe. They are two readings of the SAME
 * hotel and are assumed consistent; if they ever disagree (e.g. Player labels a 2yo "child"
 * but the probed child band is [4,12]) the band governs validation and could reject the
 * passenger's true birth date. Not reconciled here by design — known limitation.
 */
function createGenderStyleFormSchema(
  variant: Exclude<TravelerVariant, 'adult'>,
  checkIn: Date,
  bands: AgeBands,
  classified: boolean
) {
  const range = ageRangeForVariant(variant, bands, classified);
  const errorKey = AGE_RANGE_ERROR_KEY[variant];

  // Relax to the loose "< 18" rule ONLY for an empty under18adult range. An empty range on
  // another variant (e.g. infant [0,-1] when a probed band has childMin===0) must keep the
  // strict refine, which rejects every date — the safe, visible failure rather than silently
  // accepting anyone under 18.
  const birthDate =
    variant === 'under18adult' && isEmptyRange(range)
      ? birthDateValidator.refine(
          (date) => calculateAgeAtDate(parseGermanDateLocal(date), checkIn) < ADULT_MIN_AGE,
          errorKey
        )
      : birthDateValidator.refine((date) => {
          const age = calculateAgeAtDate(parseGermanDateLocal(date), checkIn);
          return age >= range.minAge && age <= range.maxAge;
        }, errorKey);

  return ChildTravelerFormSchema.extend({ birthDate });
}

/**
 * Build the vee-validate form schema for a traveler variant.
 *
 * @param variant - The 4-way traveler variant (from deriveVariant).
 * @param checkIn - Travel start date; age is validated at this date.
 * @param bands - The hotel's age bands (probe-derived or fallback).
 * @param classified - Whether the passenger carries a hotel `hotelClass`. When `false`
 *   (binary/plain URL), the `child` variant accepts any non-adult (floor 0) instead of
 *   `[childMin, childMax]` — see ageBands.ageRangeForVariant.
 */
export function createTravelerFormSchema(
  variant: TravelerVariant,
  checkIn: Date,
  bands: AgeBands,
  classified = true
) {
  return variant === 'adult'
    ? createAdultFormSchema(checkIn)
    : createGenderStyleFormSchema(variant, checkIn, bands, classified);
}

export type TravelerFormSchema = ReturnType<typeof createTravelerFormSchema>;
